import { colorFields, productCardStyleField } from "./_shared";
/**
 * Category tabs — one product carousel with a row of category tabs above it.
 * Picking a tab swaps the products underneath without leaving the page.
 * Ported from the EasyOrders `mcp-multi-cat` block found on the `prime`
 * family (2026-07-20).
 *
 * A NEW type rather than a variant, and the two obvious homes were checked
 * first. Both fail on CARDINALITY, which is the load-bearing difference:
 *
 *  - `category-sections` is the closest RENDERING — their panel is literally
 *    `section.ab-plist.mcp-multi-cat__panel`, i.e. the very same `ab-plist`
 *    carousel our `slider` layout is a port of. But `category-sections` takes
 *    `categoryId` (`single: true`), ONE category, and its whole content model
 *    is "the products of that category". This block's content model is N
 *    categories of which one is visible at a time. Adding a second, plural
 *    `categoryIds` field to `category-sections` would mean every one of its
 *    twelve layouts had to declare which of the two pickers it reads, and the
 *    inspector would show a category field that does nothing for eleven of
 *    them.
 *  - `collections` does take many categories, but it renders them AS the
 *    content — cards, chips, a ticker. Here the categories are the CONTROL and
 *    the products are the content.
 *
 * So the content model (an ordered list of categories, one selected, products
 * of the selection) has no home. This is the fifth new type the workstream has
 * added, after `shop-the-look`, `highlight-headline`, `marquee-band`,
 * `scroll-reveal` and `search-prompt`.
 *
 * Their block ships its stylesheet INLINE on the page, so every value in the
 * renderer is the author's own rather than a computed approximation:
 *
 *   section    padding `8px 0 0` (<769) / `24px 0 0` (>=769)
 *   head       centred column, gap 8, margin-bottom 20
 *   title      28px/w800 (<769) · 32px (>=769)
 *   subtitle   15px at .72 opacity
 *   tabs-wrap  overflow-x auto, scrollbar hidden, margin-inline -21px + pad 21
 *   tabs       nowrap gap 14 (<769) · WRAP gap 12 (>=769)
 *   tab        15px/w600 at .55 opacity, line-height 26
 *              (>=769) 16px, padding `5px 10px`
 *   active     full opacity, w700, a 0.075rem underline grown from width 0
 *   panel      `padding: 20px 0 10px`
 *
 * MEASURED on prime-books, the tabs row is 26px at every width below 769 and
 * 36px at and above it, and the section is 396px at 1440 with an 8-card
 * carousel underneath.
 *
 * Their tab underline animates `width` 0 → 100% over .25s. Ported as a
 * transform-scaled underline instead so it composites, and dropped entirely
 * under `prefers-reduced-motion`.
 *
 * Their tabs are `<button>`s already, so unlike most ports in this workstream
 * no keyboard affordance had to be added — but they carry no ARIA. Ours wires
 * the real `role="tablist"` / `role="tab"` / `aria-selected` / `aria-controls`
 * relationships and arrow-key roving focus, which theirs does not.
 */
export const categoryTabsSchema = {
    type: "category-tabs",
    name: { ar: "تبويبات الفئات", en: "Category tabs" },
    description: {
        ar: "صف تبويبات فوق كاروسيل منتجات — كل تبويب بيعرض فئة.",
        en: "A row of category tabs above one product carousel.",
    },
    // Their page ships one. Two on a page would read as a mistake rather than a
    // rhythm — unlike `category-sections`, which is deliberately repeatable.
    maxPerTemplate: 2,
    availableOnTemplates: ["home"],
    settings: [
        {
            id: "categoryIds",
            type: "categoryPicker",
            // The tabs ARE the categories, in the order picked.
            maxItems: 8,
            label: { ar: "الفئات", en: "Categories" },
            helpText: {
                ar: "كل فئة بتبقى تبويب. الترتيب هنا هو ترتيب التبويبات.",
                en: "Each category becomes a tab, in the order you pick them.",
            },
        },
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "عنوان القسم", en: "Section title" },
            helpText: {
                ar: "سيبه فاضي عشان يختفي — تبقى التبويبات لوحدها.",
                en: "Leave blank to hide it and show the tabs on their own.",
            },
            validate: { max: 60 },
        },
        {
            id: "subtitle",
            type: "text",
            i18n: true,
            label: { ar: "سطر تحت العنوان", en: "Subtitle" },
            validate: { max: 120 },
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            default: 2,
        },
        {
            id: "productsPerTab",
            type: "range",
            label: { ar: "عدد المنتجات في التبويب", en: "Products per tab" },
            helpText: {
                ar: "أقصى عدد منتجات بيظهر في الكاروسيل لكل فئة.",
                en: "How many products the carousel shows for each category.",
            },
            min: 4,
            max: 16,
            step: 1,
            // MEASURED: their active panel carries 8 `ab-plist-card`s.
            default: 8,
        },
        productCardStyleField(),
        {
            id: "showViewAll",
            type: "checkbox",
            label: { ar: "زرار «شوف الكل»", en: "“View all” link" },
            helpText: {
                ar: "لينك بيروح لصفحة الفئة المختارة.",
                en: "A link through to the selected category's own page.",
            },
            default: true,
        },
        ...colorFields(),
    ],
};
//# sourceMappingURL=category-tabs.js.map